import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import AdminFormLayout from "../components/AdminFormLayout";
import ButtonComponent from "../../components/ButtonComponent";
import { apiUrl } from "../../config/api";
import { ensureAdminAuth, getApiErrorMessage } from "../../utils/adminApi";

const MAX_RECOMMENDED_MB = 15;

const AdminHomeVideo = () => {
  const [loading, setLoading] = useState(true);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");
  const [newFile, setNewFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(apiUrl("/api/settings"))
      .then(({ data }) => setCurrentVideoUrl(data?.data?.homeVideoUrl || ""))
      .catch(() => toast.error("Failed to load current home video."))
      .finally(() => setLoading(false));
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setNewFile(file || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newFile) {
      toast.error("Please choose a video file first.");
      return;
    }

    try {
      const headers = ensureAdminAuth();
      const formData = new FormData();
      formData.append("video", newFile);

      setSaving(true);
      const { data } = await axios.put(apiUrl("/api/settings/home-video"), formData, {
        headers,
        timeout: 180000, // videos are bigger than images, give it real time
      });

      setCurrentVideoUrl(data.data.homeVideoUrl);
      setNewFile(null);
      toast.success("Home video updated successfully!");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to update home video"));
    } finally {
      setSaving(false);
    }
  };

  const newFileSizeMb = newFile ? (newFile.size / (1024 * 1024)).toFixed(1) : null;
  const newFileTooBig = newFile && newFile.size > MAX_RECOMMENDED_MB * 1024 * 1024;

  if (loading) {
    return (
      <AdminFormLayout title="Home Video" backTo="/admin" backLabel="Back to Dashboard">
        <p>Loading current video...</p>
      </AdminFormLayout>
    );
  }

  return (
    <AdminFormLayout title="Home Video" backTo="/admin" backLabel="Back to Dashboard">
      <p className="text-muted">
        This video plays as the background of the home page. Uploading a new one
        replaces the current video everywhere on the site immediately.
      </p>

      {currentVideoUrl && (
        <div className="admin-form-group">
          <label>Current Video</label>
          <video
            key={currentVideoUrl}
            src={currentVideoUrl}
            controls
            muted
            style={{ width: "100%", maxWidth: "480px", borderRadius: "8px", display: "block" }}
          />
        </div>
      )}

      <form onSubmit={handleSubmit} encType="multipart/form-data">
        <div className="admin-form-group">
          <label htmlFor="video">Upload New Video</label>
          <input
            type="file"
            className="form-control admin-all-forms-inputs"
            id="video"
            name="video"
            accept="video/*"
            onChange={handleFileChange}
          />
          <small className="text-muted d-block mt-2">
            For the smoothest home page loading, keep videos under ~{MAX_RECOMMENDED_MB}MB.
            Larger files (up to 100MB) are accepted, but will load more slowly for visitors.
          </small>
          {newFile && (
            <small className={newFileTooBig ? "d-block mt-1 text-danger" : "d-block mt-1 text-muted"}>
              Selected: {newFile.name} ({newFileSizeMb}MB)
              {newFileTooBig ? " — larger than recommended, consider compressing it first." : ""}
            </small>
          )}
        </div>

        <ButtonComponent type="submit" disabled={saving}>
          {saving ? "Uploading..." : "Save New Video"}
        </ButtonComponent>
      </form>
    </AdminFormLayout>
  );
};

export default AdminHomeVideo;
